import {
  AGENTCORE_CHAT_BACKEND,
  AGENT_MESH_CHAT_BACKEND,
  FASB_CHAT_BACKEND,
  SOUTHWEST_AGENTCORE_DEMO_CHAT_BACKEND,
  getWorkspaceChatBackend,
  workspaceSupportsChatWithoutKnowledgeBase,
} from './workspace-chat';

export type WorkspaceCapabilityInput = {
  name?: string | null;
  template_name?: string | null;
  workspace_config?: Record<string, unknown> | null;
};

export type WorkspaceCapabilities = {
  chatBackend: string;
  workspaceKind: 'rag' | 'agent_mesh' | 'agentcore' | 'fasb' | 'southwest_agentcore_demo' | 'unknown';
  overviewEyebrow: string;
  supportsNativeRagKnowledgeBases: boolean;
  supportsAgentMeshPlanner: boolean;
  supportsAgentMeshConnectorAccess: boolean;
  supportsStructuredWarehouseScope: boolean;
  supportsOntologyAuthoring: boolean;
  supportsContextPacks: boolean;
  supportsVerifiedQueries: boolean;
  supportsMcpExposure: boolean;
  supportsAgentCoreRuntimeSettings: boolean;
  supportsAgentCoreDocumentUpload: boolean;
  supportsRagPromptTemplateSettings: boolean;
  supportsChatWithoutKnowledgeBase: boolean;
  mcpToolSupport: Record<string, boolean>;
};

export function getWorkspaceCapabilities(
  workspace: WorkspaceCapabilityInput | null | undefined,
): WorkspaceCapabilities {
  const chatBackend = getWorkspaceChatBackend(workspace);
  const templateName = String(workspace?.template_name || '').trim().toLowerCase();
  const workspaceName = String(workspace?.name || '').trim().toLowerCase();

  const isAgentMesh = chatBackend === AGENT_MESH_CHAT_BACKEND;
  const isAgentCore = chatBackend === AGENTCORE_CHAT_BACKEND;
  const isFasb = chatBackend === FASB_CHAT_BACKEND;
  const isSouthwestDemo = chatBackend === SOUTHWEST_AGENTCORE_DEMO_CHAT_BACKEND;
  const isUnsupportedLegacyTemplate = templateName === 'data_analytics';
  const isNativeRag =
    !isAgentMesh &&
    !isAgentCore &&
    !isFasb &&
    !isSouthwestDemo &&
    !isUnsupportedLegacyTemplate &&
    (templateName === 'rag_retrieval' || chatBackend === 'native_ragflow' || workspaceName === '');

  const workspaceKind: WorkspaceCapabilities['workspaceKind'] = isAgentMesh
    ? 'agent_mesh'
    : isAgentCore
      ? 'agentcore'
      : isFasb
        ? 'fasb'
        : isSouthwestDemo
        ? 'southwest_agentcore_demo'
        : isNativeRag
          ? 'rag'
          : 'unknown';

  const supportsNativeRagKnowledgeBases = isNativeRag || isAgentMesh;
  const supportsStructuredWarehouseScope = isAgentMesh;
  const mcpToolSupport = buildMcpToolSupport({
    supportsNativeRagKnowledgeBases,
    supportsStructuredWarehouseScope,
  });

  return {
    chatBackend,
    workspaceKind,
    overviewEyebrow: overviewEyebrowForKind(workspaceKind),
    supportsNativeRagKnowledgeBases,
    supportsAgentMeshPlanner: isAgentMesh,
    supportsAgentMeshConnectorAccess: isAgentMesh,
    supportsStructuredWarehouseScope,
    supportsOntologyAuthoring: isAgentMesh,
    supportsContextPacks: isAgentMesh,
    supportsVerifiedQueries: isAgentMesh,
    supportsMcpExposure: true,
    supportsAgentCoreRuntimeSettings: isAgentCore,
    supportsAgentCoreDocumentUpload: isAgentCore,
    supportsRagPromptTemplateSettings: isNativeRag || isFasb || isAgentMesh,
    supportsChatWithoutKnowledgeBase: workspaceSupportsChatWithoutKnowledgeBase(workspace),
    mcpToolSupport,
  };
}

function buildMcpToolSupport({
  supportsNativeRagKnowledgeBases,
  supportsStructuredWarehouseScope,
}: {
  supportsNativeRagKnowledgeBases: boolean;
  supportsStructuredWarehouseScope: boolean;
}): Record<string, boolean> {
  return {
    list_workspaces: true,
    select_workspace: true,
    chat: true,
    ask_question: true,
    list_data_connections: true,
    trigger_sync: true,
    suggest_correction: true,
    structured_query: supportsStructuredWarehouseScope,
    list_knowledge_bases: supportsNativeRagKnowledgeBases,
    search_documents: supportsNativeRagKnowledgeBases,
    get_document: supportsNativeRagKnowledgeBases,
    upload_document: supportsNativeRagKnowledgeBases,
  };
}

function overviewEyebrowForKind(kind: WorkspaceCapabilities['workspaceKind']): string {
  if (kind === 'agent_mesh') return 'Workspace - Agent Mesh Context';
  if (kind === 'agentcore') return 'Workspace - AgentCore Runtime';
  if (kind === 'fasb') return 'Workspace - FASB Retrieval';
  if (kind === 'southwest_agentcore_demo') return 'Workspace - AgentCore Demo';
  if (kind === 'rag') return 'Workspace - RAG Retrieval';
  return 'Workspace';
}
